import React,{useEffect, useRef, useState} from 'react'
import { useNavigate } from 'react-router-dom';
import { searchScreenData } from '../../../../services/searchScreenData';
import AdminDashboardScreensTable from '../AdminDashboardScreensTable/AdminDashboardScreensTable'
import { useSelector } from 'react-redux'
import { getToken } from '../../../../features/token/tokenSaver';
import { updateAllScreens } from '../../../../services/updateAllScreens';
import { useFleetScreensQuery } from '../../../../query/dashboardQueries';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { SCREEN_DISPLAY_LIMITS, truncateDisplay } from '../../../../utils/screenDisplayUtils';
import { isScreenActive } from '../../../../utils/screenActiveStatus';
import { buildScreenMapMarkerIcon } from '../../../../utils/screenMapMarkerIcon';

export default function AdminDashboardScreenHome(props) {
  const navigate = useNavigate();
  const [searchItems, setSearchItems] = useState(null);
  const [selectedMapScreenId, setSelectedMapScreenId] = useState(null);
  const { data: fleetData, isLoading: isMapLoading, refetch: refetchFleet } = useFleetScreensQuery();
  const networkScreens = fleetData?.screens || [];
  const [isUpdatingAllScreens, setIsUpdatingAllScreens] = useState(false);
  const [actionFeedback, setActionFeedback] = useState({ type: '', message: '' });
  const [lastSearched, setLastSearched] = useState("");
  const token = useSelector(getToken)
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});

    useEffect(() => {
      return () => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.remove();
          mapInstanceRef.current = null;
        }
      }
    }, [])

  function toNumber(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function escapePopupText(value) {
    if (value === undefined || value === null) {
      return '';
    }
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /** Map marker popup: id + live status only (full details are on the admin screen page). */
  function buildMapPopupHtml(screen) {
    const active = isScreenActive(screen);
    return (
      `<div class="admin-map-popup">`
      + `<strong>Screen #${escapePopupText(screen.screenId)}</strong>`
      + `<p style="margin:6px 0 0;">Status: ${active ? '🟢 Active' : '⚫ Inactive'}</p>`
      + `</div>`
    );
  }

  async function loadMapScreens() {
    await refetchFleet();
  }

  function upsertMarkers() {
    if (!mapInstanceRef.current) {
      return;
    }

    Object.values(markersRef.current).forEach((marker) => {
      mapInstanceRef.current.removeLayer(marker);
    });
    markersRef.current = {};

    const validScreens = networkScreens.filter((screen) => {
      return toNumber(screen.latitude) !== null && toNumber(screen.longitude) !== null;
    });

    validScreens.forEach((screen) => {
      const lat = toNumber(screen.latitude);
      const lng = toNumber(screen.longitude);
      const active = isScreenActive(screen);
      const markerIcon = buildScreenMapMarkerIcon({
        isActive: active,
        type: screen.type ?? screen.Type,
      });

      const marker = L.marker([lat, lng], { icon: markerIcon })
        .bindPopup(buildMapPopupHtml(screen))
        .on('click', () => {
          openScreenDetails(screen.screenId);
        })
        .addTo(mapInstanceRef.current);

      markersRef.current[screen.screenId] = marker;

      if (selectedMapScreenId === screen.screenId) {
        marker.openPopup();
      }
    });

    if (validScreens.length > 0) {
      const bounds = L.latLngBounds(validScreens.map((screen) => [toNumber(screen.latitude), toNumber(screen.longitude)]));
      mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
    }
  }

  useEffect(() => {
    if (!mapContainerRef.current || mapInstanceRef.current) {
      return;
    }

    mapInstanceRef.current = L.map(mapContainerRef.current).setView([39.5, -98.35], 4);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
      referrerPolicy: 'strict-origin-when-cross-origin',
    }).addTo(mapInstanceRef.current);
  }, []);

  useEffect(() => {
    upsertMarkers();
  }, [networkScreens]);

  useEffect(() => {
    if (!selectedMapScreenId) {
      return;
    }

    Object.entries(markersRef.current).forEach(([screenId, marker]) => {
      if (String(screenId) !== String(selectedMapScreenId)) {
        marker.closePopup?.();
      }
    });

    const selectedMarker = markersRef.current[selectedMapScreenId];
    if (selectedMarker) {
      selectedMarker.openPopup();
    }
  }, [selectedMapScreenId]);

  function searchHandler(e) {
    if (e.keyCode === 13 && lastSearched !== e.target.value && !e.target.value.match(/^\s*$/)) {
      searchScreenData(token, e.target.value).then(response => {
        setSearchItems(response.data.screens);
        setLastSearched(e.target.value);
      })
    }
    else if (e.target.value.match(/^\s*$/)){
      setSearchItems(null);
    }
  }
  function openScreenDetails(screenId) {
    navigate(`/adminDashboard/screens/details/${screenId}`);
  }

  function extractBackendError(error) {
    return error?.response?.data?.message
      || error?.response?.data?.error
      || error?.message
      || 'Unable to queue screen updates right now.';
  }

  async function handleUpdateAllScreens() {
    if (isUpdatingAllScreens) {
      return;
    }

    setIsUpdatingAllScreens(true);
    setActionFeedback({ type: '', message: '' });

    try {
      const response = await updateAllScreens(token);
      const payload = response?.data || {};
      const updatedCount = payload.updatedCount ?? payload.count ?? payload.updated ?? payload.screensUpdated;

      if (updatedCount !== undefined && updatedCount !== null) {
        setActionFeedback({ type: 'success', message: `${updatedCount} screens queued for update.` });
      } else {
        setActionFeedback({ type: 'success', message: 'All screens queued for update.' });
      }
    } catch (error) {
      setActionFeedback({ type: 'error', message: extractBackendError(error) });
    } finally {
      setIsUpdatingAllScreens(false);
    }
  }

  const activeCount = networkScreens.filter((screen) => isScreenActive(screen)).length;

    return (
      <div className='adminScreensHome'>
        <div className='adminScreensHeader'>
          <div>
            <span className='registeredScreensTitle'>Registered Screens</span>
            <span className='adminScreensHeaderSubtext'>Live map + operational health for your complete screen fleet.</span>
          </div>
          <div className='adminScreensActions'>
            <button className='adminScreensSecondaryButton' onClick={handleUpdateAllScreens} disabled={isUpdatingAllScreens}>
              {isUpdatingAllScreens ? 'Queueing...' : 'Update All Screens'}
            </button>
            <button className='adminScreensRefreshButton' onClick={loadMapScreens} disabled={isMapLoading}>
              {isMapLoading ? 'Refreshing...' : 'Refresh Map Data'}
            </button>
          </div>
        </div>

        {actionFeedback.message && (
          <div className={`adminScreensFeedback ${actionFeedback.type === 'error' ? 'error' : 'success'}`}>
            {actionFeedback.message}
          </div>
        )}

        <div className='adminScreensKpis'>
          <div className='adminScreensKpiCard'>
            <span className='adminScreensKpiLabel'>Total Screens</span>
            <span className='adminScreensKpiValue'>{networkScreens.length}</span>
          </div>
          <div className='adminScreensKpiCard'>
            <span className='adminScreensKpiLabel'>Active Now</span>
            <span className='adminScreensKpiValue'>{activeCount}</span>
          </div>
        </div>

        <div className='adminScreensMapPanel'>
          <div className='adminScreensMapCanvas' ref={mapContainerRef}></div>
          <div className='adminScreensMapList'>
            <span className='adminScreensMapListTitle'>Network Screens</span>
            <div className='adminScreensMapListContent'>
              {networkScreens.map((screen) => {
                const isActive = isScreenActive(screen);
                const metaLine = [screen.brand || screen.Brand, screen.type || screen.Type].filter(Boolean).join(' · ');
                return (
                  <div className='adminScreensMapListRow' key={screen.screenId}>
                    <button
                      type='button'
                      className={`adminScreensMapListItem ${selectedMapScreenId === screen.screenId ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedMapScreenId(screen.screenId);
                        const marker = markersRef.current[screen.screenId];
                        if (marker && mapInstanceRef.current) {
                          mapInstanceRef.current.flyTo(marker.getLatLng(), 14, { duration: 0.7 });
                          marker.openPopup();
                        }
                      }}
                    >
                      <span className={`adminScreensMapDot ${isActive ? 'active' : 'inactive'}`}></span>
                      <span className='adminScreensMapScreenId'>#{screen.screenId}</span>
                      <span className='adminScreensMapAddress' title={screen.address || ''}>
                        {truncateDisplay(screen.address || 'Unknown location', SCREEN_DISPLAY_LIMITS.addressLine)}
                      </span>
                      {metaLine && (
                        <span className='adminScreensMapListMeta' title={metaLine}>
                          {truncateDisplay(metaLine, SCREEN_DISPLAY_LIMITS.brandTypeLine)}
                        </span>
                      )}
                    </button>
                    <button
                      type='button'
                      className='adminScreensMapListMoreBtn'
                      aria-label='Screen details'
                      title='Screen details'
                      onClick={() => openScreenDetails(screen.screenId)}
                    >
                      ⋯
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className='adminDashboardScreenTable'>
          <span className='registeredScreensTitle'>Registered Screens</span>
          <input onKeyUp={(e) => { searchHandler(e); }} type={"text"} placeholder='Search' className='adminDashboardScreenPageSearchInput' />
          <AdminDashboardScreensTable screenDetails={openScreenDetails} data={searchItems}></AdminDashboardScreensTable>
        </div>
      </div>
  )
}
